/**
* SlowQueryMonitor - Visa och analysera slow queries från MariaDB
*/
import React, { useState, useEffect } from'react';
import { useNavigate } from'react-router-dom';
import { ArrowPathIcon, TrashIcon,
ClockIcon,
ExclamationTriangleIcon,
ChevronDownIcon,
ChevronRightIcon,
DocumentDuplicateIcon,
ArrowLeftIcon,
} from'@heroicons/react/24/outline';
import { toast } from'react-hot-toast';
import { useTranslation } from'@/store/languageStore';
import api from'@/services/api/client';
import { ROUTES } from '@/constants/routes';
interface SlowQuery {
start_time: string;
user_host: string;
query_time: string;
query_time_sec: number;
lock_time: string;
rows_sent: number;
rows_examined: number;
db: string;
sql_text: string;
}
interface SlowQueryData {
queries: SlowQuery[];
stats: {
total_queries: number;
avg_query_time: number;
max_query_time: number;
total_rows_examined: number;
};
settings: {
slow_query_log: string;
log_output: string;
long_query_time: string;
message?: string;
};
}
const SlowQueryMonitor: React.FC = () => {
const { t } = useTranslation();
const navigate = useNavigate();
const [data, setData] = useState<SlowQueryData | null>(null);
const [loading, setLoading] = useState(true);
const [savingSettings, setSavingSettings] = useState(false);
const [hours, setHours] = useState(24);
const [expandedRow, setExpandedRow] = useState<number | null>(null);
const [showSettings, setShowSettings] = useState(false);
const [settingsForm, setSettingsForm] = useState({
enabled: true,
longQueryTime:'1',
logOutput:'TABLE',
});
const loadQueries = async () => {
try {
setLoading(true);
const response = await api.get(`/api/sysadmin/slow-queries?hours=${hours}&limit=200`);
if (response.data.success) {
setData(response.data.data);
}
} catch {
toast.error(t('sysadmin.loadSlowQueriesError'));
} finally {
setLoading(false);
}
};
useEffect(() => {
loadQueries();
}, [hours]);
const handleClearLog = async () => {
if (!confirm(t('copy.sysadmin.clearSlowLogConfirm'))) return;
try {
await api.delete('/api/sysadmin/slow-queries');
toast.success(t('copy.sysadmin.slowLogCleared'));
loadQueries();
} catch (err) {
toast.error(t('sysadmin.clearLogError'));
}
};
const handleUpdateSettings = async () => {
if (savingSettings) return; // Prevent double-click
try {
setSavingSettings(true);
const response = await api.put('/api/sysadmin/slow-queries/settings', {
enabled: settingsForm.enabled,
longQueryTime: parseFloat(settingsForm.longQueryTime),
logOutput: settingsForm.logOutput,
});
if (response.data.success) {
toast.success(t('copy.sysadmin.settingsUpdated'));
setShowSettings(false);
// Vänta lite innan vi laddar queries igen för att ge servern tid att uppdatera
setTimeout(() => {
loadQueries();
}, 1000);
} else {
toast.error(response.data.error || t('copy.sysadmin.settingsUpdateFailed'));
}
} catch (err: any) {
const errorMessage = err.response?.data?.error || err.message || t('copy.sysadmin.settingsUpdateFailed');
// Visa mer detaljerat felmeddelande om det är ett behörighetsfel
if (errorMessage.includes('permission') || errorMessage.includes('SUPER') || errorMessage.includes('SYSTEM_VARIABLES_ADMIN')) {
toast.error(
t('copy.sysadmin.slowQueryPermissionError', { command: 'mysql -u root -p < /home/ubuntu/DQFleet/backend/scripts/grant-slow-query-permissions.sql' }),
{ duration: 10000 }
);
} else {
toast.error(errorMessage);
}
} finally {
setSavingSettings(false);
}
};
// Uppdatera formulär när data laddas
useEffect(() => {
if (data?.settings) {
setSettingsForm({
enabled: data.settings.slow_query_log ==='ON',
longQueryTime: data.settings.long_query_time ||'1',
logOutput: data.settings.log_output ||'FILE',
});
}
}, [data]);
const copyToClipboard = (text: string) => {
navigator.clipboard.writeText(text);
toast.success(t('common.copiedToClipboard'));
};
const formatTime = (dateStr: string) => {
const date = new Date(dateStr);
return date.toLocaleString('sv-SE');
};
const formatQueryTime = (seconds: number) => {
if (seconds < 1) return `${(seconds * 1000).toFixed(0)} ms`;
if (seconds < 60) return `${seconds.toFixed(2)} s`;
return `${(seconds / 60).toFixed(1)} ${t('copy.sysadmin.minutesShort')}`;
};
const getQueryTimeColor = (seconds: number) => {
if (seconds > 10) return'text-red-600 bg-red-50';
if (seconds > 5) return'text-orange-600 bg-orange-50';
if (seconds > 2) return'text-yellow-600 bg-yellow-50';
return'text-textSecondary bg-bgContainer';
};
const highlightSQL = (sql: string) => {
// Enkel SQL-highlighting
const keywords = ['SELECT','FROM','WHERE','JOIN','LEFT','RIGHT','INNER','OUTER', 'ON','AND','OR','ORDER BY','GROUP BY','LIMIT','OFFSET','INSERT','UPDATE', 'DELETE','SET','VALUES','INTO','AS','IN','NOT','NULL','IS','LIKE','BETWEEN',
'COUNT','SUM','AVG','MAX','MIN','DISTINCT','HAVING','UNION','ALL'];
let highlighted = sql;
keywords.forEach(kw => {
const regex = new RegExp(`\\b${kw}\\b`,'gi');
highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${kw}</span>`);
});
return highlighted;
};
if (loading && !data) {
return (
<div className="h-full flex items-center justify-center">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
</div>
);
}
return (
<div className="h-full flex flex-col bg-bgLight overflow-hidden">
{/* Header */}
<div className="bg-bgElevated border-b border-border px-4 py-3">
<div className="flex items-center justify-between flex-wrap gap-2">
<div className="flex items-center gap-4">
<button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
<ArrowLeftIcon className="w-5 h-5 text-textDefault"/>
</button>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<ClockIcon className="w-6 h-6 text-primary"/>
Slow Query Monitor
</h1>
{/* Tidsfilter */}
<select
value={hours}
onChange={(e) => setHours(parseInt(e.target.value))}
className="px-3 py-1.5 border border-border rounded-lg bg-bgBase text-sm"
>
<option value={1}>{t('copy.sysadmin.lastHour')}</option>
<option value={6}>{t('copy.sysadmin.last6Hours')}</option>
<option value={24}>{t('copy.sysadmin.last24Hours')}</option>
<option value={48}>{t('copy.sysadmin.last48Hours')}</option>
<option value={168}>{t('copy.sysadmin.lastWeek')}</option>
</select>
</div>
<div className="flex items-center gap-2">
<button
onClick={() => setShowSettings(!showSettings)}
className="px-3 py-1.5 bg-bgContainer0 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 text-sm"
>
{t('copy.sysadmin.settingsGear')}
</button>
<button
onClick={loadQueries}
disabled={loading}
className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2 text-sm"
>
<ArrowPathIcon className={`h-4 w-4 ${loading ?'animate-spin' :''}`} />
{t('common.refresh')}
</button>
<button
onClick={handleClearLog}
className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 text-sm"
>
<TrashIcon className="h-4 w-4"/>
{t('copy.sysadmin.clearLog')}
</button>
</div>
</div>
{/* Inställningar och statistik */}
{data && (
<div className="mt-3 flex flex-wrap gap-4 text-sm">
<div className="flex items-center gap-2 bg-bgBase px-3 py-1 rounded">
<span className="text-textSecondary">Slow log:</span>
<span className={data.settings.slow_query_log ==='ON' ?'text-green-600 font-semibold' :'text-red-600'}>
{data.settings.slow_query_log}
</span>
</div>
<div className="flex items-center gap-2 bg-bgBase px-3 py-1 rounded">
<span className="text-textSecondary">{t('copy.sysadmin.threshold')}</span>
<span className="font-semibold">{data.settings.long_query_time}s</span>
</div>
<div className="flex items-center gap-2 bg-bgBase px-3 py-1 rounded">
<span className="text-textSecondary">Output:</span>
<span className="font-semibold">{data.settings.log_output}</span>
</div>
{data.stats && (
<>
<div className="flex items-center gap-2 bg-orange-100 dark:bg-orange-900/30 px-3 py-1 rounded">
<span className="text-textSecondary">{t('copy.sysadmin.countLabel')}</span>
<span className="font-semibold text-orange-700">{data.stats.total_queries || 0}</span>
</div>
{data.stats.avg_query_time && (
<div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 px-3 py-1 rounded">
<span className="text-textSecondary">{t('copy.sysadmin.average')}</span>
<span className="font-semibold text-yellow-700">
{formatQueryTime(data.stats.avg_query_time)}
</span>
</div>
)}
{data.stats.max_query_time && (
<div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/30 px-3 py-1 rounded">
<span className="text-textSecondary">{t('copy.sysadmin.max')}</span>
<span className="font-semibold text-red-700">
{formatQueryTime(data.stats.max_query_time)}
</span>
</div>
)}
</>
)}
</div>
)}
{data?.settings.message && (
<div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
<ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"/>
<p className="text-sm text-yellow-800">{data.settings.message}</p>
</div>
)}
{/* Inställningsformulär */}
{showSettings && (
<div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
<h3 className="text-sm font-semibold text-textDefault mb-3">{t('copy.sysadmin.slowQuerySettings')}</h3>
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
<label className="block text-xs text-textSecondary mb-1">
Slow Query Log
</label>
<select
value={settingsForm.enabled ?'ON' :'OFF'}
onChange={(e) => setSettingsForm({ ...settingsForm, enabled: e.target.value ==='ON' })}
className="w-full px-3 py-1.5 border border-border rounded-lg bg-bgBase text-sm"
>
<option value="ON">{t('copy.sysadmin.on')}</option>
<option value="OFF">{t('copy.sysadmin.off')}</option>
</select>
</div>
<div>
<label className="block text-xs text-textSecondary mb-1">
{t('copy.sysadmin.thresholdSeconds')}
</label>
<input
type="number"
step="0.1"
min="0.1"
value={settingsForm.longQueryTime}
onChange={(e) => setSettingsForm({ ...settingsForm, longQueryTime: e.target.value })}
className="w-full px-3 py-1.5 border border-border rounded-lg bg-bgBase text-sm"
placeholder="1.0"
/>
<p className="text-xs text-textSecondary mt-1">{t('copy.sysadmin.recommendedOneSecond')}</p>
</div>
<div>
<label className="block text-xs text-textSecondary mb-1">
Log Output
</label>
<select
value={settingsForm.logOutput}
onChange={(e) => setSettingsForm({ ...settingsForm, logOutput: e.target.value })}
className="w-full px-3 py-1.5 border border-border rounded-lg bg-bgBase text-sm"
>
<option value="TABLE">{t('copy.sysadmin.logOutputTable')}</option>
<option value="FILE">{t('copy.sysadmin.logOutputFile')}</option>
<option value="TABLE,FILE">{t('copy.sysadmin.logOutputBoth')}</option>
</select>
<p className="text-xs text-textSecondary mt-1">{t('copy.sysadmin.tableRequiredHint')}</p>
</div>
</div>
<div className="mt-4 flex gap-2">
<button
onClick={handleUpdateSettings}
disabled={savingSettings}
className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
>
{savingSettings ? (
<>
<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
{t('common.saving')}
</>
) : (
t('copy.sysadmin.saveSettings')
)}
</button>
<button
onClick={() => setShowSettings(false)}
disabled={savingSettings}
className="px-4 py-2 bg-gray-300 text-textDefault rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
>
{t('common.cancel')}
</button>
</div>
{parseFloat(settingsForm.longQueryTime) < 1 && (
<div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
{t('copy.sysadmin.thresholdWarning')}
</div>
)}
</div>
)}
</div>
{/* Queries lista */}
<div className="flex-1 overflow-auto p-4">
{data?.queries.length === 0 ? (
<div className="text-center py-12 text-textSecondary">
<ClockIcon className="h-16 w-16 mx-auto mb-4 text-textTertiary"/>
<p className="text-lg">{t('copy.sysadmin.noSlowQueries')}</p>
<p className="text-sm mt-1">{t('copy.sysadmin.slowQueryLoggedHint', { seconds: data.settings.long_query_time })}</p>
</div>
) : (
<div className="space-y-2">
{data?.queries.map((query, index) => (
<div
key={index}
className="bg-bgElevated rounded-lg shadow border border-border overflow-hidden"
>
{/* Rad-header (klickbar) */}
<div
onClick={() => setExpandedRow(expandedRow === index ? null : index)}
className="flex items-center gap-4 p-3 cursor-pointer hover:bg-bgBase/50"
>
<div className="flex-shrink-0">
{expandedRow === index ? (
<ChevronDownIcon className="h-5 w-5 text-textTertiary"/>
) : (
<ChevronRightIcon className="h-5 w-5 text-textTertiary"/>
)}
</div>
<div className="flex-shrink-0 w-44 text-sm text-textSecondary">
{formatTime(query.start_time)}
</div>
<div className={`flex-shrink-0 px-2 py-0.5 rounded text-sm font-mono ${getQueryTimeColor(query.query_time_sec)}`}>
{formatQueryTime(query.query_time_sec)}
</div>
<div className="flex-shrink-0 w-24 text-sm text-textSecondary font-mono">
{query.db ||'-'}
</div>
<div className="flex-1 text-sm text-textDefault truncate font-mono">
{query.sql_text?.substring(0, 100)}...
</div>
<div className="flex-shrink-0 text-xs text-textTertiary">
{query.rows_examined?.toLocaleString()} rows
</div>
</div>
{/* Expanderad vy */}
{expandedRow === index && (
<div className="border-t border-border bg-bgLight p-4">
<div className="flex justify-between items-start mb-3">
<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
<div>
<span className="text-textSecondary">{t('copy.sysadmin.queryTime')}</span>
<span className="ml-2 font-semibold">{query.query_time}</span>
</div>
<div>
<span className="text-textSecondary">{t('copy.sysadmin.lockTime')}</span>
<span className="ml-2 font-semibold">{query.lock_time}</span>
</div>
<div>
<span className="text-textSecondary">{t('copy.sysadmin.rowsSent')}</span>
<span className="ml-2 font-semibold">{query.rows_sent?.toLocaleString()}</span>
</div>
<div>
<span className="text-textSecondary">{t('copy.sysadmin.rowsExamined')}</span>
<span className="ml-2 font-semibold">{query.rows_examined?.toLocaleString()}</span>
</div>
<div>
<span className="text-textSecondary">{t('copy.sysadmin.user')}</span>
<span className="ml-2 font-mono text-xs">{query.user_host}</span>
</div>
</div>
<button
onClick={() => copyToClipboard(query.sql_text)}
className="px-3 py-1.5 bg-bgBase text-textDefault rounded hover:bg-bgBase flex items-center gap-2 text-sm"
>
<DocumentDuplicateIcon className="h-4 w-4"/>
{t('copy.sysadmin.copySql')}
</button>
</div>
<div className="mt-3">
<p className="text-xs text-textSecondary mb-1 font-semibold">SQL Query:</p>
<pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap"
dangerouslySetInnerHTML={{ __html: highlightSQL(query.sql_text ||'') }}
/>
</div>
</div>
)}
</div>
))}
</div>
)}
</div>
</div>
);
};
export default SlowQueryMonitor;
